<template>
  <!-- Barre de navigation fixe -->
  <header class="topbar" :class="{ 'topbar-scrolled': isScrolled }">
    <div class="topbar-inner">
      <a class="brand" @click="handleLogoClick" role="button" tabindex="0" @keyup.enter="handleLogoClick">
        <span class="brand-mark">&lt;/&gt;</span>
        <span class="brand-name">Alexis <span class="brand-dot"></span> Dupain</span>
      </a>

      <nav class="topbar-links" aria-label="Navigation principale">
        <a v-for="link in links" :key="link.label" class="topbar-link" :class="{ active: isActive(link) }" @click="go(link)">
          {{ link.label }}
        </a>
        <router-link v-if="isAdmin" to="/dashboard" class="topbar-link" active-class="active">Dashboard</router-link>
      </nav>

      <div class="topbar-actions">
        <template v-if="isLoggedIn">
          <button @click="navigateToTop('/profile')" class="btn-ghost-sm" title="Profil">
            <i class="bi bi-person-circle"></i><span>Profil</span>
          </button>
          <button @click="logout" class="btn-icon-sm" title="Déconnexion" aria-label="Déconnexion">
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </template>
        <a class="btn-gradient-sm" @click="navigateToTop('/contact')">Me contacter</a>
      </div>

      <!-- Bouton menu burger (mobile) -->
      <button class="burger-menu-btn" @click="toggleMenu" aria-label="Ouvrir le menu" :aria-expanded="isMenuOpen">
        <i class="bi" :class="isMenuOpen ? 'bi-x-lg' : 'bi-list'"></i>
      </button>
    </div>
  </header>

  <!-- Overlay pour fermer le menu en cliquant à l'extérieur -->
  <transition name="overlay-fade">
    <div v-if="isMenuOpen" class="sidebar-overlay" @click="closeMenu"></div>
  </transition>

  <!-- Menu mobile -->
  <transition name="sidebar-slide">
    <nav v-if="isMenuOpen" class="sidebar" aria-label="Menu mobile">
      <div class="sidebar-top">
        <span class="brand-name">Alexis<span class="brand-dot"> </span>Dupain</span>
        <button class="btn-icon-sm" @click="closeMenu" aria-label="Fermer le menu"><i class="bi bi-x-lg"></i></button>
      </div>

      <ul class="sidebar-menu">
        <li v-for="link in links" :key="link.label">
          <a @click="go(link)" class="sidebar-link" :class="{ active: isActive(link) }">
            <i class="bi" :class="link.icon"></i>
            <span>{{ link.label }}</span>
          </a>
        </li>
        <li v-if="isAdmin">
          <router-link to="/dashboard" class="sidebar-link" active-class="active" @click="closeMenu">
            <i class="bi bi-speedometer2"></i>
            <span>Tableau de bord</span>
          </router-link>
        </li>
      </ul>

      <div class="sidebar-user">
        <template v-if="isLoggedIn">
          <button @click="navigateToTop('/profile')" class="sidebar-btn">
            <i class="bi bi-person-circle"></i>
            <span>Profil</span>
          </button>
          <button @click="logout" class="sidebar-btn btn-logout">
            <i class="bi bi-box-arrow-right"></i>
            <span>Déconnexion</span>
          </button>
        </template>
      </div>
    </nav>
  </transition>
</template>

<script>
import { getCurrentProfile, onAuthChange, signOut } from '../../lib/auth'

export default {
  name: 'NavBar',
  data() {
    return {
      isMenuOpen: false,
      isLoggedIn: false,
      isAdmin: false,
      isScrolled: false,
      links: [
        { label: 'À propos', icon: 'bi-person', section: 'apropos' },
        { label: 'Parcours', icon: 'bi-mortarboard', section: 'formations' },
        { label: 'Projets', icon: 'bi-folder', route: '/projets' },
        { label: 'Compétences', icon: 'bi-stars', route: '/competences' },
        { label: 'Contact', icon: 'bi-envelope', route: '/contact' }
      ]
    }
  },
  mounted() {
    this.checkLoginStatus()
    this.unsubscribeAuth = onAuthChange(this.checkLoginStatus)
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.handleScroll()
  },
  beforeUnmount() {
    if (this.unsubscribeAuth) this.unsubscribeAuth()
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 8
    },
    go(link) {
      if (link.section) this.scrollToSection(link.section)
      else this.navigateToTop(link.route)
    },
    isActive(link) {
      return !!link.route && this.$route.path === link.route
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    closeMenu() {
      this.isMenuOpen = false
    },
    async checkLoginStatus() {
      try {
        const profile = await getCurrentProfile()
        this.isLoggedIn = !!profile
        this.isAdmin = profile?.role === 'admin'
      } catch (err) {
        console.error('Erreur de vérification de session:', err)
        this.isLoggedIn = false
        this.isAdmin = false
      }
    },
    async logout() {
      await signOut()
      this.isLoggedIn = false
      this.isAdmin = false
      this.closeMenu()
      this.$router.push('/')
      console.log('Déconnexion réussie')
    },
    scrollToSection(sectionId) {
      this.closeMenu()
      if (this.$route.path === '/') {
        this.smoothScrollTo(sectionId)
      } else {
        this.$router.push('/').then(() => {
          this.$nextTick(() => {
            setTimeout(() => {
              this.smoothScrollTo(sectionId)
            }, 100)
          })
        })
      }
    },
    smoothScrollTo(sectionId) {
      const element = document.getElementById(sectionId)
      if (element) {
        const navbarHeight = 88
        const elementPosition = element.offsetTop - navbarHeight
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        })
      }
    },
    navigateToTop(route) {
      this.closeMenu()
      this.$router.push(route).then(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
          }, 100)
        })
      }).catch(error => {
        console.error('Erreur de navigation:', error)
      })
    },
    handleLogoClick() {
      // Normal logo click: navigate to home/top
      this.navigateToTop('/')
    }
  }
}
</script>

<!-- styles moved to src/components/style.css per project structure -->
